import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';import { AuthProvider, useAuth } from './context/AuthContext';
import { PermissionsProvider, usePermissions } from './context/PermissionsContext';
import { UploadProvider } from './context/UploadContext';
import { SocketProvider } from './context/SocketContext';
import { ThemeProvider, useTheme } from './context/ThemeContext';
import { isFinanceAdmin, isDepartmentManager } from './utils/roleHelpers';

// Pages
import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import AdminDashboard from './pages/AdminDashboard';
import PlatformDashboard from './pages/PlatformDashboard';
import RolePermissions from './pages/RolePermissions';
import InvoiceList from './pages/InvoiceList';
import InvoiceCreate from './pages/InvoiceCreate';
import InvoiceDetail from './pages/InvoiceDetail';
import InvoiceUpload from './pages/InvoiceUpload';
import OCRQueuePage from './pages/OCRQueuePage';
import DeletedInvoices from './pages/DeletedInvoices';
import ApprovalQueue from './pages/ApprovalQueue';
import DepartmentApprovalQueue from './pages/DepartmentApprovalQueue';
import UsersList from './pages/UsersList';
import UserDepartments from './pages/UserDepartments';
import VendorsList from './pages/VendorsList';
import DepartmentVendors from './pages/DepartmentVendors';
import Subscriptions from './pages/Subscriptions';
import BudgetsList from './pages/BudgetsList';
import BudgetDashboardRouter from './pages/BudgetDashboardRouter';
import DepartmentBudgetManagement from './pages/DepartmentBudgetManagement';
import BudgetHeaders from './pages/BudgetHeaders';
import BudgetMonthlyView from './pages/BudgetMonthlyView';
import BudgetAnalytics from './pages/BudgetAnalytics';
import ProjectLines from './pages/ProjectLines';
import CompaniesList from './pages/CompaniesList';
import CompanyDetail from './pages/CompanyDetail';
import PlatformPricing from './pages/PlatformPricing';
import DepartmentsList from './pages/DepartmentsList';
import ProcessesMap from './pages/ProcessesMap';
import AuditLogs from './pages/AuditLogs';
import UserActivity from './pages/UserActivity';
import LicenseStatus from './pages/LicenseStatus';
import PlatformResources from './pages/PlatformResources';
import Settings from './pages/Settings';
import Reports from './pages/Reports';
import GLAccountsSettings from './pages/GLAccountsSettings';
import ProjectsSettings from './pages/ProjectsSettings';
import CurrenciesSettings from './pages/CurrenciesSettings';
import ManagerSettings from './pages/ManagerSettings';
import ActivityFeedPage from './pages/ActivityFeedPage';
import ExchangeRates from './pages/ExchangeRates';
import MileageRateSettings from './pages/MileageRateSettings';
import ExchangeRateReconciliation from './pages/ExchangeRateReconciliation';
import NotificationPreferences from './pages/NotificationPreferences';
import ExpenseList from './pages/ExpenseList';
import ExpenseDetail from './pages/ExpenseDetail';
import ExpenseSubmit from './pages/ExpenseSubmit';
import ExpenseEdit from './pages/ExpenseEdit';
import ExpenseApprovalQueue from './pages/ExpenseApprovalQueue';
import VATClaimsDashboard from './pages/VATClaimsDashboard';
import FinanceDashboardPage from './pages/FinanceDashboardPage';
import FinancialYears from './pages/FinancialYears';
import FinanceIntegrationSettings from './pages/FinanceIntegrationSettings';import InvoicePostingPage from './pages/InvoicePostingPage';
import AccrualsPage from './pages/AccrualsPage';
import AllDocumentsPage from './pages/AllDocumentsPage';
import SpendAnalyticsPage from './pages/SpendAnalyticsPage';
import ApprovalSLAManagement from './pages/ApprovalSLAManagement';
import SchedulerPage from './pages/SchedulerPage';
import EmailTestPage from './pages/EmailTestPage';
import PurchaseOrderList from './pages/PurchaseOrderList';
import PurchaseOrderDetail from './pages/PurchaseOrderDetail';
import PurchaseOrderCreate from './pages/PurchaseOrderCreate';
import CompanySettings from './pages/CompanySettings';
import ApiTokensSettings from './pages/ApiTokensSettings';
import Help from './pages/Help';
import HelpArticle from './pages/HelpArticle';
import HelpManage from './pages/HelpManage';
import SupportTicket from './pages/SupportTicket';
import SupportTicketsAdmin from './pages/SupportTicketsAdmin';
import CompanyTeamsSettings from './pages/CompanyTeamsSettings';
import AIStandup from './pages/AIStandup';
import PlatformTeamsUsage from './pages/PlatformTeamsUsage';
import PlatformVoiceUsage from './pages/PlatformVoiceUsage';
import VoiceAgentGuide from './pages/VoiceAgentGuide';
import VoiceCallLogs from './pages/VoiceCallLogs';
import VoiceQueries from './pages/VoiceQueries';

// Layout
import Layout from './components/Layout';
import PermissionGuard from './components/PermissionGuard';
import ModuleGate from './modules/ModuleGate';

// Protected Route wrapper
const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  return isAuthenticated ? children : <Navigate to="/login" replace />;
};

// Role-based dashboard router
const DashboardRouter = () => {
  const { hasPermission, loading: permissionsLoading, permissions } = usePermissions();
  const { user } = useAuth();
  
  // Wait for permissions to load
  if (permissionsLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="text-gray-600">Loading permissions...</div>
      </div>
    );
  }
  
  // Platform Owner or Super Admin - render PlatformDashboard
  if (user?.isSuperAdmin) {
    return <PlatformDashboard />;
  }
  
  // Company Admin (has manage_users permission OR role is 'Company Admin')
  if (hasPermission('manage_users') || user?.role === 'Company Admin') {
    return <Navigate to="/admin" replace />;
  }
  
  // Finance Admin → Finance Dashboard as landing page
  if (isFinanceAdmin(user)) {
    return <Navigate to="/finance-dashboard" replace />;
  }
  
  // Department Manager → invoices view (dept-scoped)
  if (isDepartmentManager(user)) {
    return <Navigate to="/invoices" replace />;
  }

  // Expense-only user (has view_my_expenses but not view_invoices) → My Expenses as landing page
  if (hasPermission('view_my_expenses') && !hasPermission('view_invoices')) {
    return <Navigate to="/expenses" replace />;
  }
  
  // Regular company user
  return <Dashboard />;
};

// Role-based vendors router
const VendorsRouter = () => {
  const { hasPermission, loading: permissionsLoading } = usePermissions();
  
  if (permissionsLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="text-gray-600">Loading...</div>
      </div>
    );
  }
  
  // Only Finance/Admin users with manage_vendors permission see full vendors page
  const canManageVendors = hasPermission('manage_vendors');
  
  if (canManageVendors) {
    return <VendorsList />;
  }
  
  // Department Managers and others see simplified department vendors view
  return <DepartmentVendors />;
};

function AppContent() {
  // Sync DB theme preference when user loads after login
  const { user } = useAuth();
  const { setTheme } = useTheme();
  React.useEffect(() => {
    if (user?.theme === 'light' || user?.theme === 'dark') {
      setTheme(user.theme);
    }
  }, [user?.theme]);

  return (
    <Routes>
      {/* Public routes */}
      <Route path="/login" element={<Login />} />

      {/* Protected routes */}
      <Route
        path="/"
        element={
          <ProtectedRoute>
            <Layout />
          </ProtectedRoute>
        }
      >
        <Route index element={<DashboardRouter />} />
        <Route path="admin" element={<PermissionGuard permission="manage_users"><AdminDashboard /></PermissionGuard>} />
        <Route path="invoices" element={<InvoiceList />} />
        <Route path="invoices/new" element={<PermissionGuard permission="create_invoices"><InvoiceCreate /></PermissionGuard>} />
        <Route path="invoices/upload" element={<PermissionGuard permission="upload_invoices"><InvoiceUpload /></PermissionGuard>} />
        <Route path="invoices/queue" element={<PermissionGuard permission="manage_finance_settings"><OCRQueuePage /></PermissionGuard>} />
        <Route path="invoices/deleted" element={<PermissionGuard permission="delete_invoices"><DeletedInvoices /></PermissionGuard>} />
        <Route path="invoices/:id" element={<InvoiceDetail />} />
        <Route path="approvals" element={<PermissionGuard permission="approve_invoices"><ApprovalQueue /></PermissionGuard>} />
        <Route path="department-approvals" element={<PermissionGuard permission="approve_invoices"><DepartmentApprovalQueue /></PermissionGuard>} />
        <Route path="expenses" element={<ExpenseList />} />
        <Route path="expenses/submit" element={<PermissionGuard permission="submit_expenses"><ExpenseSubmit /></PermissionGuard>} />
        <Route path="expenses/:id/edit" element={<PermissionGuard permission="submit_expenses"><ExpenseEdit /></PermissionGuard>} />
        <Route path="expenses/:id" element={<ExpenseDetail />} />
        <Route path="expense-approvals" element={<PermissionGuard permission="approve_expenses"><ExpenseApprovalQueue /></PermissionGuard>} />
        <Route path="vat-claims" element={<PermissionGuard permission="view_finance_reports"><VATClaimsDashboard /></PermissionGuard>} />
        <Route path="finance-dashboard" element={<PermissionGuard permission="view_finance_reports"><FinanceDashboardPage /></PermissionGuard>} />
        <Route path="spend-analytics" element={<PermissionGuard permission="view_finance_reports"><SpendAnalyticsPage /></PermissionGuard>} />
        <Route path="users" element={<UsersList />} />
        <Route path="user-departments" element={<PermissionGuard permission="manage_users"><UserDepartments /></PermissionGuard>} />
        <Route path="departments" element={<PermissionGuard permission="manage_departments"><DepartmentsList /></PermissionGuard>} />
        <Route path="audit-logs" element={<PermissionGuard permission="view_audit_logs"><AuditLogs /></PermissionGuard>} />
        <Route path="user-activity" element={<PermissionGuard permission="manage_users"><UserActivity /></PermissionGuard>} />
        <Route path="license-status" element={<LicenseStatus />} />
        <Route path="vendors" element={<VendorsRouter />} />
        <Route path="subscriptions" element={<PermissionGuard permissions={['view_invoices', 'manage_vendors']} allowDeptManager><Subscriptions /></PermissionGuard>} />
        <Route path="manager-settings" element={<PermissionGuard permission="manage_departments"><ManagerSettings /></PermissionGuard>} />
        <Route path="activity-feed" element={<PermissionGuard permission="view_audit_logs"><ActivityFeedPage /></PermissionGuard>} />
        <Route path="manage-budget" element={<ModuleGate module="budgets"><PermissionGuard permissions={['view_budgets', 'manage_budgets']}><BudgetDashboardRouter /></PermissionGuard></ModuleGate>} />
        <Route path="budget-headers" element={<ModuleGate module="budgets"><PermissionGuard permission="manage_budgets"><BudgetHeaders /></PermissionGuard></ModuleGate>} />
        <Route path="budget-lines" element={<ModuleGate module="budgets"><BudgetsList /></ModuleGate>} />
        <Route path="budget-monthly-view" element={<ModuleGate module="budgets"><PermissionGuard permission="view_budgets"><BudgetMonthlyView /></PermissionGuard></ModuleGate>} />
        <Route path="budget-analytics" element={<ModuleGate module="budgets"><PermissionGuard permission="view_budgets"><BudgetAnalytics /></PermissionGuard></ModuleGate>} />
        <Route path="reports" element={<Reports />} />
        <Route path="role-permissions" element={<PermissionGuard permission="manage_users"><RolePermissions /></PermissionGuard>} />
        <Route path="gl-accounts" element={<PermissionGuard permission="manage_finance_settings"><GLAccountsSettings /></PermissionGuard>} />
        <Route path="projects" element={<ModuleGate module="projects"><PermissionGuard permission="manage_finance_settings"><ProjectsSettings /></PermissionGuard></ModuleGate>} />
        <Route path="project-lines" element={<ModuleGate module="projects"><PermissionGuard permission="manage_finance_settings"><ProjectLines /></PermissionGuard></ModuleGate>} />
        <Route path="currencies" element={<PermissionGuard permission="manage_finance_settings"><CurrenciesSettings /></PermissionGuard>} />
        <Route path="exchange-rates" element={<PermissionGuard permission="manage_finance_settings"><ExchangeRates /></PermissionGuard>} />
        <Route path="mileage-rates" element={<PermissionGuard permission="manage_finance_settings"><MileageRateSettings /></PermissionGuard>} />
        <Route path="exchange-rate-reconciliation" element={<PermissionGuard permission="manage_finance_settings"><ExchangeRateReconciliation /></PermissionGuard>} />
        <Route path="notification-preferences" element={<NotificationPreferences />} />
        <Route path="financial-years" element={<PermissionGuard permission="manage_finance_settings"><FinancialYears /></PermissionGuard>} />
        <Route path="finance-integrations" element={<ModuleGate module="integrations"><PermissionGuard permission="manage_company_settings"><FinanceIntegrationSettings /></PermissionGuard></ModuleGate>} />
        <Route path="approval-slas" element={<PermissionGuard permission="manage_finance_settings"><ApprovalSLAManagement /></PermissionGuard>} />
        <Route path="invoice-posting" element={<PermissionGuard permission="manage_finance_settings"><InvoicePostingPage /></PermissionGuard>} />
        <Route path="accruals" element={<PermissionGuard permission="manage_finance_settings"><AccrualsPage /></PermissionGuard>} />
        <Route path="scheduler" element={<ModuleGate module="scheduler"><PermissionGuard permission="manage_finance_settings"><SchedulerPage /></PermissionGuard></ModuleGate>} />
        <Route path="email-test" element={<PermissionGuard requireSuperAdmin><EmailTestPage /></PermissionGuard>} />
        <Route path="all-documents" element={<PermissionGuard permission="view_invoices"><AllDocumentsPage /></PermissionGuard>} />
        <Route path="companies" element={<PermissionGuard requireSuperAdmin><CompaniesList /></PermissionGuard>} />
        <Route path="companies/:id" element={<PermissionGuard requireSuperAdmin><CompanyDetail /></PermissionGuard>} />
        <Route path="processesmap" element={<PermissionGuard requireSuperAdmin><ProcessesMap /></PermissionGuard>} />
        <Route path="platform/pricing" element={<PermissionGuard requireSuperAdmin><PlatformPricing /></PermissionGuard>} />
        <Route path="platform/resources" element={<PermissionGuard requireSuperAdmin><PlatformResources /></PermissionGuard>} />
        <Route path="settings" element={<PermissionGuard permission="manage_system"><Settings /></PermissionGuard>} />
        <Route path="company-settings" element={<PermissionGuard permission="manage_company_settings"><CompanySettings /></PermissionGuard>} />
        <Route path="api-tokens" element={<PermissionGuard permission="view_api_tokens"><ApiTokensSettings /></PermissionGuard>} />
        {/* Purchase Orders */}
        <Route path="purchase-orders" element={<ModuleGate module="purchase_orders"><PermissionGuard permissions={['view_purchase_orders', 'manage_finance_settings']} allowDeptManager><PurchaseOrderList /></PermissionGuard></ModuleGate>} />
        <Route path="purchase-orders/create" element={<ModuleGate module="purchase_orders"><PermissionGuard permissions={['create_purchase_orders', 'manage_finance_settings']} allowDeptManager><PurchaseOrderCreate /></PermissionGuard></ModuleGate>} />
        <Route path="purchase-orders/:id" element={<ModuleGate module="purchase_orders"><PermissionGuard permissions={['view_purchase_orders', 'manage_finance_settings']} allowDeptManager><PurchaseOrderDetail /></PermissionGuard></ModuleGate>} />
        {/* Help Center */}
        <Route path="help" element={<Help />} />
        <Route path="help/manage" element={<PermissionGuard permission="manage_help"><HelpManage /></PermissionGuard>} />
        <Route path="help/manage/:id" element={<PermissionGuard permission="manage_help"><HelpManage /></PermissionGuard>} />
        <Route path="help/:slug" element={<HelpArticle />} />
        <Route path="support/new" element={<SupportTicket />} />
        <Route path="support/tickets" element={<PermissionGuard permission="manage_help"><SupportTicketsAdmin /></PermissionGuard>} />
        {/* Teams */}
        <Route path="teams-settings" element={<ModuleGate module="teams"><PermissionGuard permission="manage_users"><CompanyTeamsSettings /></PermissionGuard></ModuleGate>} />
        <Route path="ai-standup" element={<ModuleGate module="teams"><AIStandup /></ModuleGate>} />
        <Route path="platform/teams-usage" element={<PermissionGuard requireSuperAdmin><PlatformTeamsUsage /></PermissionGuard>} />
        <Route path="platform/voice-usage" element={<PermissionGuard requireSuperAdmin><PlatformVoiceUsage /></PermissionGuard>} />
        <Route path="voice-agent-guide" element={<VoiceAgentGuide />} />
        <Route path="voice-call-logs" element={<PermissionGuard permission="manage_finance_settings"><VoiceCallLogs /></PermissionGuard>} />
        <Route path="voice-queries" element={<PermissionGuard permission="manage_finance_settings"><VoiceQueries /></PermissionGuard>} />
      </Route>

      {/* Catch all */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <BrowserRouter>
      <ThemeProvider>
        <AuthProvider>
          <PermissionsProvider>
            <SocketProvider>
              <UploadProvider>
                <AppContent />
              </UploadProvider>
            </SocketProvider>
          </PermissionsProvider>
        </AuthProvider>
      </ThemeProvider>
    </BrowserRouter>
  );
}

export default App;
